import { getAuth } from "firebase/auth";

export function getApiBase() {
  // Production on Netlify:
  if (process.env.NODE_ENV === "production") {
    return "/.netlify/functions";
  }

  // Local dev with `netlify functions:serve --port 9998`
  return "http://127.0.0.1:9998/.netlify/functions";
}

/**
 * Call Netlify Function with Firebase ID token.
 * - Always pulls a fresh token from Firebase (handles expiry).
 * - Throws with useful error text if response is not ok.
 *
 * Usage:
 *  callNetlifyFunction("me", { method: "GET" })
 *  callNetlifyFunction(`firmware-browse?prefix=${encodeURIComponent(prefix)}`, { method: "GET" })
 */
export async function callNetlifyFunction(
  name,
  { method = "POST", body } = {},
) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Nejste přihlášen(a).");

  // Force refresh token for reliability in dev
  const idToken = await user.getIdToken(true);

  const url = `${getApiBase()}/${name}`;

  const headers = {
    Authorization: `Bearer ${idToken}`,
  };

  // Only send JSON headers/body when we actually have a body
  let fetchBody;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    fetchBody = JSON.stringify(body);
  }

  const res = await fetch(url, {
    method,
    headers,
    body: fetchBody,
  });

  if (!res.ok) {
    let detail = "";
    try {
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const j = await res.json();
        detail = j?.error
          ? `${j.error}${j?.message ? `: ${j.message}` : ""}`
          : JSON.stringify(j);
      } else {
        detail = await res.text();
      }
    } catch {
      // ignore
    }
    throw new Error(`API chyba (${res.status})${detail ? ` – ${detail}` : ""}`);
  }

  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Sign a download URL (and log audit on backend).
 * Returns: { key, downloadUrl, expiresIn }
 */
export async function signFirmwareDownload(key) {
  if (!key) throw new Error("Chybí key souboru pro stažení.");
  return callNetlifyFunction("firmware-sign-download", {
    method: "POST",
    body: { key },
  });
}

/**
 * Request signed upload URL to R2 (and log UPLOAD_REQUEST).
 * payload must match firmware-sign-upload requirements.
 * Returns: { key, uploadUrl, expiresIn }
 */
export async function signFirmwareUpload(payload) {
  return callNetlifyFunction("firmware-sign-upload", {
    method: "POST",
    body: payload,
  });
}

/**
 * Create Firestore metadata after successful PUT upload (and log UPLOAD_CONFIRMED).
 * Returns: { ok, docId, data }
 */
export async function createFirmwareMetadata(payload) {
  return callNetlifyFunction("firmware-create-metadata", {
    method: "POST",
    body: payload,
  });
}
